import {Action, Store, Unsubscribe} from 'redux';

import {AppStore} from './appStore';
import {BatchCoordinator} from './batchCoordinator';

/*
 * State.
 */

const batchCoordinator = new BatchCoordinator();

/*
 * Api.
 */

export function batchStore<R>(callback: () => R): R {
  try {
    return batchCoordinator.runInBatch(callback);
  } finally {
    batchCoordinator.maybeFinalizeBatch();
  }
}

export function buildBatchedStore<S, A extends Action>(store: AppStore): AppStore {
  return {
    dispatch: store.dispatch.bind(store),
    getState: store.getState.bind(store),
    subscribe: (listener: () => void) => buildBatchedSubscribe(store, listener),
    replaceReducer: store.replaceReducer.bind(store),
    [Symbol.observable]: store[Symbol.observable].bind(store),
  };
}

/** For testing, reset the batch coordinator. */
export function resetBatchCoordinator() {
  batchCoordinator.reset();
}

/*
 * Helpers.
 */

function buildBatchedSubscribe<S, A extends Action, TStore extends Store<S, A>>(
  store: TStore,
  listener: () => void,
): Unsubscribe {
  const unsubscribeStore = store.subscribe(() => {
    if (batchCoordinator.isBatching()) {
      // If we're in the middle of a batch, skip calling the listener.
      return;
    }

    // Otherwise, if we're not currently batching, call the listener.
    listener();
  });
  const unsubscribeBatchEnd = batchCoordinator.subscribeToBatchEnd(() => {
    // Call the listener when the batch is over.
    listener();
  });

  return () => {
    unsubscribeStore();
    unsubscribeBatchEnd();
  };
}
